import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import type { Point } from '@rydres/shared';
import { AddressInput, Button, Field, InfoTip, Input, Notice, PlaceButtons, TextArea, fmtPhone, toE164, useAddressTarget, type PickedAddress } from '@rydres/ui';
import { driverApi, geoApi } from '../lib/api.js';
import { useProfile } from '../lib/profile.js';
import type { Passenger } from '../lib/types.js';

const nowLocal = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** One screen, one tap: a cash or Zelle trip gets its waybill and lands in the ledger. */
export function LogTrip() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const address = useAddressTarget();
  const [f, setF] = useState({
    riderName: '',
    riderPhone: '',
    passengers: 1,
    pickupAddress: '',
    dropoffAddress: '',
    pickupPoint: null as Point | null,
    dropoffPoint: null as Point | null,
    startsAt: nowLocal(),
    price: '',
    paymentMode: 'cash',
    arrangedVia: 'phone call',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string | number) => setF((x) => ({ ...x, [k]: v }));
  const setPlace = (field: 'pickup' | 'dropoff', p: PickedAddress) => setF((x) => ({ ...x, [`${field}Address`]: p.address, [`${field}Point`]: p.point }));
  const pick = (value: string, point: Point | null) => {
    setPlace(address.target, { address: value, point });
    if (address.target === 'pickup' && !f.dropoffAddress) address.setTarget('dropoff');
  };
  const [saved, setSaved] = useState<Passenger[]>([]);
  useEffect(() => {
    driverApi.passengers().then((r) => setSaved(r.passengers), () => undefined);
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const phone = f.riderPhone ? toE164(f.riderPhone) : null;
    if (f.riderPhone && !phone) return setError('That phone number does not look right.');
    setBusy(true);
    try {
      const r = await driverApi.log({
        riderName: f.riderName,
        riderPhone: phone,
        passengers: Number(f.passengers),
        pickupAddress: f.pickupAddress,
        dropoffAddress: f.dropoffAddress,
        pickupPoint: f.pickupPoint,
        dropoffPoint: f.dropoffPoint,
        startsAt: new Date(f.startsAt).toISOString(),
        priceCents: Math.round(Number(f.price) * 100),
        paymentMode: f.paymentMode,
        arrangedVia: f.arrangedVia,
        notes: f.notes || null,
      });
      navigate(`/bookings/${r.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the trip.');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <h1 className="flex items-center gap-2 text-2xl font-black">
        Log a trip
        <InfoTip label="Log a trip">For rides booked by phone, text or in person and paid outside the page. Free; the waybill is produced for you.</InfoTip>
      </h1>
      {saved.length > 0 && (
        <Field label="Saved passenger (optional)">
          <select
            defaultValue=""
            onChange={(e) => {
              const p = saved.find((x) => x.id === e.target.value);
              if (p) setF((x) => ({ ...x, riderName: p.name ?? '', riderPhone: p.phone }));
            }}
            className="w-full rounded-xl bg-card2 px-4 py-3 text-base outline-none"
          >
            <option value="">Choose from your passengers…</option>
            {saved.map((p) => <option key={p.id} value={p.id}>{p.name ?? 'No name'} · {fmtPhone(p.phone)}</option>)}
          </select>
        </Field>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Passenger name"><Input required value={f.riderName} onChange={(e) => set('riderName', e.target.value)} /></Field>
        <Field label="Passenger phone (optional)"><Input inputMode="tel" value={f.riderPhone} onChange={(e) => set('riderPhone', e.target.value)} /></Field>
        <Field label="Pickup"><AddressInput required geo={geoApi} near={f.dropoffPoint} value={f.pickupAddress} onChange={(p) => setPlace('pickup', p)} onFocus={() => address.setTarget('pickup')} /></Field>
        <Field label="Drop-off"><AddressInput required geo={geoApi} near={f.pickupPoint} value={f.dropoffAddress} onChange={(p) => setPlace('dropoff', p)} onFocus={() => address.setTarget('dropoff')} /></Field>
        <PlaceButtons places={profile?.places ?? []} target={address.target} onPick={pick} />
        {profile && profile.places.length === 0 && (
          <p className="text-xs text-muted">Save the addresses you use often under <Link to="/services" className="font-bold underline">Services → Your places</Link> to fill these with one tap.</p>
        )}
        <Field label="Pickup time"><Input type="datetime-local" required value={f.startsAt} onChange={(e) => set('startsAt', e.target.value)} /></Field>
        <Field label="Passengers"><Input type="number" min={1} max={14} value={f.passengers} onChange={(e) => set('passengers', Number(e.target.value))} /></Field>
        <Field label="Price (per vehicle, $)"><Input type="number" step="0.01" min="0" required value={f.price} onChange={(e) => set('price', e.target.value)} /></Field>
        <Field label="Paid by">
          <select value={f.paymentMode} onChange={(e) => set('paymentMode', e.target.value)} className="w-full rounded-xl bg-card2 px-4 py-3 text-base outline-none">
            <option value="cash">Cash</option>
            <option value="zelle">Zelle</option>
            <option value="direct">Other (Venmo, invoice…)</option>
          </select>
        </Field>
        <Field label="How it was arranged" hint="Goes on the waybill">
          <select value={f.arrangedVia} onChange={(e) => set('arrangedVia', e.target.value)} className="w-full rounded-xl bg-card2 px-4 py-3 text-base outline-none">
            <option>phone call</option>
            <option>text message</option>
            <option>email</option>
            <option>in person, in advance</option>
            <option>repeat client, standing arrangement</option>
          </select>
        </Field>
        <Field label="Notes (optional)"><TextArea value={f.notes} onChange={(e) => set('notes', e.target.value)} /></Field>
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Button type="submit" disabled={busy} className="w-full">{busy ? 'Saving…' : 'Save trip and make waybill'}</Button>
    </form>
  );
}
